// ANETT HORVATH — Content Blocks Admin + Enhanced House Control
// Master admin for all content blocks with inline editing + membership gating.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/database';
import { SUPPORTED_LANGUAGES } from '../../config/house.config';
import type { SupportedLang } from '../../config/house.config';
import { getAllContentBlocks, upsertBlock, deleteBlock, getLastUpdate, type ContentBlock, type ContentBlockType } from '../../store/content-blocks';
import { getAllMedia, upsertMedia, softDeleteMedia, restoreMedia, type MediaItem, FALLBACK_IMAGE } from '../../store/media-library';
import type { VisibilityStatus, MembershipAccess } from '../../core/render-policy';
import { getHouseRuntimeRecord, updateFullHouseState, HOUSE_PHASES, HOUSE_MODES, type HousePhase, type HouseMode } from '../../modules/house-state';

const Btn = ({ children, onClick, v = 'p' }: { children: React.ReactNode; onClick: () => void; v?: 'p' | 'd' | 'g' }) => {
  const c = v === 'p' ? 'bg-charcoal text-ivory hover:bg-charcoal/90' : v === 'd' ? 'bg-burgundy text-ivory hover:bg-burgundy/80' : 'text-charcoal/40 hover:text-charcoal border border-charcoal/10';
  return <button onClick={onClick} className={`px-4 py-2 text-[10px] tracking-widest uppercase font-sans transition-colors ${c}`}>{children}</button>;
};

const VIS_OPTIONS: VisibilityStatus[] = ['visible', 'hidden', 'draft', 'archived'];
const ACCESS_OPTIONS: MembershipAccess[] = ['public', 'waitlist', 'member', 'inner_circle', 'admin_only'];
const TYPE_OPTIONS: ContentBlockType[] = ['text', 'image', 'video', 'section', 'product', 'link'];

// CONTENT BLOCKS ADMIN
export function ContentBlocksAdmin() {
  const { t } = useTranslation();
  const store = useStore();
  const adminId = store.session?.user_id || '';
  const [blocks, setBlocks] = useState(getAllContentBlocks());
  const [editing, setEditing] = useState<ContentBlock | null>(null);
  const [filter, setFilter] = useState('');

  const reload = () => setBlocks(getAllContentBlocks());
  const filtered = blocks.filter(b => !filter || b.key.includes(filter) || b.value_en.toLowerCase().includes(filter.toLowerCase()));

  const startNew = () => setEditing({ id: '', key: '', type: 'text', value_en: '', value_hu: '', value_de: '', value_it: '', value_fr: '', media_id: null, route_target: null, visibility_status: 'visible', membership_access: 'public', manual_lock: false, sort_order: 0, updated_at: '' });

  const saveBlock = () => {
    if (!editing) return;
    try {
      upsertBlock(editing.id || null, editing);
      store._audit(adminId, editing.id ? 'content_block_updated' : 'content_block_created', 'content_block', editing.key, null, { key: editing.key });
      store._persist();
    } catch (e: any) {
      alert(e.message);
      return;
    }
    reload();
    setEditing(null);
  };

  const del = (id: string) => { deleteBlock(id); store._audit(adminId, 'content_block_deleted', 'content_block', id, null, null); store._persist(); reload(); };
  const toggleLock = (b: ContentBlock) => { upsertBlock(b.id, { manual_lock: !b.manual_lock }); store._audit(adminId, b.manual_lock ? 'content_unlocked' : 'content_locked', 'content_block', b.id, null, null); store._persist(); reload(); };

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl">{editing.id ? 'Edit Block' : 'New Block'}</h2>
          <Btn onClick={() => setEditing(null)} v="g">Cancel</Btn>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><label className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Key</label><input value={editing.key} onChange={e => setEditing({ ...editing, key: e.target.value })} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory" /></div>
          <div className="space-y-1"><label className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Type</label><select value={editing.type} onChange={e => setEditing({ ...editing, type: e.target.value as ContentBlockType })} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory">{TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
          <div className="space-y-1"><label className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Visibility</label><select value={editing.visibility_status} onChange={e => setEditing({ ...editing, visibility_status: e.target.value as VisibilityStatus })} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory">{VIS_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}</select></div>
          <div className="space-y-1"><label className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Access</label><select value={editing.membership_access} onChange={e => setEditing({ ...editing, membership_access: e.target.value as MembershipAccess })} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory">{ACCESS_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}</select></div>
          <div className="space-y-1"><label className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Route Target</label><input value={editing.route_target || ''} onChange={e => setEditing({ ...editing, route_target: e.target.value || null })} placeholder="/product" className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory" /></div>
          <div className="space-y-1"><label className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Sort Order</label><input type="number" value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: Number(e.target.value) })} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory" /></div>
        </div>
        <div className="space-y-2">
          {SUPPORTED_LANGUAGES.map(lang => (
            <div key={lang} className="border border-charcoal/5 p-3 space-y-1">
              <label className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">{lang.toUpperCase()} {!editing[`value_${lang}` as keyof ContentBlock] && lang !== 'en' && <span className="text-burgundy">⚠ missing</span>}</label>
              <textarea value={(editing[`value_${lang}` as keyof ContentBlock] as string) || ''} onChange={e => setEditing({ ...editing, [`value_${lang}`]: e.target.value })} rows={2} className="w-full border border-charcoal/10 p-2 text-sm font-sans bg-ivory resize-none" />
            </div>
          ))}
        </div>
        <div className="flex gap-2"><Btn onClick={saveBlock}>Save</Btn><Btn onClick={() => setEditing(null)} v="g">Cancel</Btn></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif text-xl">Content Blocks</h2>
        <div className="flex gap-2">
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter by key..." className="border border-charcoal/10 px-3 py-1.5 text-sm font-sans bg-ivory" />
          <Btn onClick={startNew}>+ New Block</Btn>
        </div>
      </div>
      <p className="text-[10px] text-charcoal/30 font-sans">Last update: {getLastUpdate()} · {blocks.length} blocks</p>
      <div className="space-y-1">
        {filtered.map(b => (
          <div key={b.id} className={`border p-3 flex items-center justify-between gap-2 ${b.manual_lock ? 'border-burgundy/30 bg-burgundy/5' : 'border-charcoal/5'}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs">{b.key}</span>
                <span className="text-[9px] px-1.5 py-0.5 border border-charcoal/10 text-charcoal/30">{b.type}</span>
                <span className={`text-[9px] px-1.5 py-0.5 ${b.visibility_status === 'visible' ? 'bg-green-50 text-green-600' : 'bg-charcoal/5 text-charcoal/30'}`}>{b.visibility_status}</span>
                <span className="text-[9px] px-1.5 py-0.5 bg-gold/10 text-gold">{b.membership_access}</span>
                {b.manual_lock && <span className="text-[9px] px-1.5 py-0.5 bg-burgundy/10 text-burgundy">🔒 LOCKED</span>}
              </div>
              <p className="text-[10px] text-charcoal/40 truncate mt-0.5">{b.value_en?.substring(0, 80) || '—'}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <Btn onClick={() => toggleLock(b)} v="g">{b.manual_lock ? 'Unlock' : 'Lock'}</Btn>
              <Btn onClick={() => setEditing(b)} v="g">Edit</Btn>
              <Btn onClick={() => del(b.id)} v="d">×</Btn>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ENHANCED MEDIA ADMIN
export function MediaLibraryAdmin() {
  const store = useStore();
  const adminId = store.session?.user_id || '';
  const [items, setItems] = useState(getAllMedia());
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState<MediaItem['type']>('image');

  const reload = () => setItems(getAllMedia());
  const add = () => { if (!url) return; upsertMedia(null, { file_url: url, label, type }); store._audit(adminId, 'media_uploaded', 'media', '', null, { url, label }); store._persist(); setUrl(''); setLabel(''); reload(); };
  const del = (id: string) => { softDeleteMedia(id); store._audit(adminId, 'media_deleted', 'media', id, null, null); store._persist(); reload(); };

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-xl">Media Library</h2>
      <div className="border border-charcoal/5 p-4 grid grid-cols-3 gap-2">
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="Image URL" className="border border-charcoal/10 p-2 text-sm font-sans bg-ivory" />
        <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label" className="border border-charcoal/10 p-2 text-sm font-sans bg-ivory" />
        <div className="flex gap-2">
          <select value={type} onChange={e => setType(e.target.value as MediaItem['type'])} className="border border-charcoal/10 p-2 text-sm font-sans bg-ivory">
            {['image','video','editorial','product','symbol','background','brand'].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <Btn onClick={add}>Add</Btn>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(m => (
          <div key={m.id} className="border border-charcoal/5 p-2 space-y-1">
            <img src={m.file_url} alt={m.label} className="w-full h-24 object-cover" onError={e => (e.currentTarget.src = FALLBACK_IMAGE)} />
            <p className="text-[10px] text-charcoal/40 font-sans truncate">{m.label}</p>
            <p className="text-[9px] text-charcoal/20 font-mono truncate">{m.type} · {m.language}</p>
            <Btn onClick={() => del(m.id)} v="d">Delete</Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

// ENHANCED HOUSE CONTROL
export function EnhancedHouseControl() {
  const store = useStore();
  const adminId = store.session?.user_id || '';
  const [state, setState] = useState(getHouseRuntimeRecord());

  const save = () => {
    updateFullHouseState(state, adminId);
    store._audit(adminId, 'house_state_changed', 'house_runtime', 'global', null, state);
    store._persist();
    setState(getHouseRuntimeRecord());
  };

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-xl">House Control</h2>
      <div className="border border-charcoal/5 p-6 space-y-4">
        <div className="space-y-2">
          <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Phase</p>
          <div className="flex flex-wrap gap-2">
            {HOUSE_PHASES.map(p => (
              <button key={p} onClick={() => setState({ ...state, house_state: p })} className={`px-4 py-2 text-[11px] tracking-widest uppercase font-sans transition-colors ${state.house_state === p ? 'bg-charcoal text-ivory' : 'border border-charcoal/10 text-charcoal/40'}`}>{p.replace('_', ' ')}</button>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Mode</p>
          <div className="flex flex-wrap gap-2">
            {HOUSE_MODES.map(m => (
              <button key={m} onClick={() => setState({ ...state, mode: m })} className={`px-4 py-2 text-[11px] tracking-widest uppercase font-sans transition-colors ${state.mode === m ? 'bg-gold text-charcoal' : 'border border-charcoal/10 text-charcoal/40'}`}>{m.replace('_', ' ')}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={state.preview_mode} onChange={e => setState({ ...state, preview_mode: e.target.checked })} className="accent-gold" />
            <span className="text-[11px] tracking-widest uppercase font-sans text-charcoal/50">Preview Mode</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-[10px] tracking-widest uppercase text-charcoal/30 font-sans">Allocation Limit</span>
            <input type="number" value={state.allocation_limit} onChange={e => setState({ ...state, allocation_limit: Number(e.target.value) })} className="w-20 border border-charcoal/10 p-1.5 text-sm font-sans bg-ivory" />
          </div>
        </div>
        <div className="flex gap-2">
          <Btn onClick={save}>Save</Btn>
        </div>
        <p className="text-[10px] text-charcoal/20 font-sans">Updated: {state.updated_at}</p>
      </div>
    </div>
  );
}
